const HIDDEN_REASONING_BLOCKS = [
  /<think\b[^>]*>[\s\S]*?<\/think>/gi,
  /<analysis\b[^>]*>[\s\S]*?<\/analysis>/gi,
  /<reasoning\b[^>]*>[\s\S]*?<\/reasoning>/gi,
];

const EVIDENCE_HEADING = /\n\s*二[、.．]\s*(?:AI\s*)?(?:背调依据(?:（?详细版）?)?|身份检索与证据比对)/i;

export const RESEARCH_STAGE_LABELS = {
  verified: { label: "核验通过", tone: "success" },
  body_fetched: { label: "取得正文 · 待核验", tone: "info" },
  found: { label: "找到来源 · 无正文", tone: "warning" },
  searched: { label: "已搜索 · 未找到来源", tone: "neutral" },
  failed: { label: "本次获取异常", tone: "danger" },
  pending: { label: "待执行 / 补充信息", tone: "neutral" },
  legacy: { label: "报告引用链接", tone: "neutral" },
};

const SOURCE_STAGE_ORDER = ["verified", "body_fetched", "found", "legacy", "failed"];

export const SOURCE_CATEGORY_LABELS = {
  industry_org: "行业组织", industry_media: "行业媒体", directory: "行业目录",
  market_data: "市场数据", b2b: "B2B 平台", social_people: "职业与社交",
  competitor_official: "竞品官网", company_official: "客户企业官网",
  business_registry: "企业注册信息", trade_data: "海关与贸易数据",
  government: "政府与监管", general_web: "通用公开来源",
};

export const TRUST_LEVEL_LABELS = { high: "高可信", medium: "中可信", low: "低可信" };

export const VERIFICATION_LEVEL_LABELS = {
  pending: "待核验",
  low: "证据较少",
  medium: "中等可信",
  high: "高可信",
  high_risk: "高风险",
};

// Keys are the flag code without its HIGH_/REVIEW_ prefix.
export const RISK_FLAG_LABELS = {
  RESEARCH_EVIDENCE_CONFLICT: "背调证据相互矛盾",
  FREE_EMAIL_PHONE_LARGE_ORDER: "免费邮箱下大额采购",
  PHONE_COUNTRY_MISMATCH: "电话区号与国家不符",
  EARLY_SENSITIVE_PRICING_REQUEST: "未报公司即索要底价或完整价格表",
  CROSS_INDUSTRY_PURCHASE: "客户主营与光伏储能无关",
  PROJECT_COMPANY_SCALE_MISMATCH: "公司规模与项目体量不符",
  PARKED_DOMAIN_EVIDENCE: "客户域名疑似停放或出售",
  DNS_UNRESOLVED_EVIDENCE: "客户域名无法解析",
  RECENT_DOMAIN_REGISTRATION: "客户域名注册不足 90 天",
  SANCTIONS_EVIDENCE: "公开来源提及制裁名单",
  BANKRUPTCY_EVIDENCE: "官方来源显示破产或注销",
  CONFIRMED_FRAUD: "已确认欺诈记录",
  BUSINESS_REGISTRY_NOT_FOUND: "工商登记未查到",
  TYPOSQUAT_DOMAIN: "邮箱域名与官网域名高度相似（疑似仿冒）",
};

export const SEVEN_STEP_VERSION = "seven_step_v1";

/** Compact line for the knowledge-base seven-step SOP; null for legacy six-step data. */
export function sevenStepSummary(compatibility) {
  const coverage = compatibility?.research_coverage;
  if (coverage?.version !== SEVEN_STEP_VERSION) return null;
  const risk = (coverage.steps || []).find((step) => step.kind === "risk") || {};
  return {
    completed: Number(coverage.completed || 0),
    total: Number(coverage.total || 7),
    segment: compatibility.segment || "U",
    redFlags: Number(risk.red_flag_count || 0),
    reviewItems: Number(risk.review_count || 0),
    gaps: (coverage.steps || []).filter((step) => !step.kind && !step.verified).map((step) => step.label),
  };
}

export const REVIEW_FLAG_LABELS = {
  REQUIRED_EMAIL_MISSING: "缺少客户邮箱",
  MESSAGE_ANOMALY: "邮件正文为空或无法识别",
  MESSAGE_PRODUCT_ANOMALY: "邮件中未识别到产品或规模",
};

export function reviewFlagLabel(flag) {
  return REVIEW_FLAG_LABELS[flag] || flag;
}

export function riskFlagLabel(flag) {
  const review = flag.startsWith("REVIEW_");
  const code = flag.replace(/^(?:REVIEW|HIGH)_/, "");
  const label = RISK_FLAG_LABELS[code] || code;
  return review ? `待核实：${label}` : label;
}

export function researchSourceStageKey(source) {
  if (source.legacy) return "legacy";
  const fetched = ["fetched", "fetched_cached"].includes(source.fetch_status);
  // Sources listed with the inquiry omit extracted_text; a fetched status is enough there.
  const body = source.acquisition?.body_fetched
    ?? (fetched && (source.extracted_text === undefined || Boolean(String(source.extracted_text || "").trim())));
  if (body && source.cross_checked && !source.conflict_detected) return "verified";
  if (body) return "body_fetched";
  if (["failed", "empty", "blocked"].includes(source.fetch_status)) return "failed";
  return "found";
}

export function researchSourceStage(source) {
  return RESEARCH_STAGE_LABELS[researchSourceStageKey(source)];
}

export function sanitizeResearchText(value) {
  let text = String(value || "").replace(/\r\n?/g, "\n").trim();
  HIDDEN_REASONING_BLOCKS.forEach((pattern) => { text = text.replace(pattern, ""); });
  text = text
    .replace(/^[\s\S]*?<\/(?:think|analysis|reasoning)>\s*/i, "")
    .replace(/<(?:think|analysis|reasoning)\b[^>]*>[\s\S]*$/i, "")
    .replace(/^```(?:analysis|thinking|reasoning)?\s*/i, "")
    .replace(/```\s*$/i, "")
    .replace(/\n{3,}/g, "\n\n");
  return text.trim();
}

export function splitResearchReport(value) {
  const text = sanitizeResearchText(value);
  if (!text) return { summary: "", evidence: "" };

  const evidenceMatch = text.match(EVIDENCE_HEADING);
  const evidenceIndex = evidenceMatch?.index ?? -1;
  const evidence = evidenceIndex >= 0 ? text.slice(evidenceIndex).trim() : "";
  const leading = evidenceIndex >= 0 ? text.slice(0, evidenceIndex).trim() : text;
  const summaryMatch = leading.match(
    /(?:【[^\n]+】[^\n]*\n)?\s*一[、.．]\s*AI\s*背调总结(?:（精炼版）)?\s*([\s\S]*)/i,
  );
  const summary = summaryMatch ? summaryMatch[1].trim() : leading;
  return { summary, evidence };
}

function normalizeHttpUrl(value) {
  const candidate = String(value || "")
    .trim()
    .replace(/[),.;，。；]+$/g, "");
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    return parsed.href;
  } catch {
    return "";
  }
}

function sourceDomain(url) {
  try {
    return new URL(url).hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
}

/**
 * Prefer persisted research sources, while retaining links embedded in reports
 * created before structured research evidence was introduced.
 */
export function collectResearchSources(value, structuredSources = []) {
  const result = [];
  const seen = new Set();
  const append = (source) => {
    const url = normalizeHttpUrl(source?.url);
    if (!url || seen.has(url)) return;
    seen.add(url);
    result.push({
      ...source,
      url,
      title: String(source?.title || source?.name || "").trim(),
      domain: String(source?.domain || sourceDomain(url)).trim(),
    });
  };

  (Array.isArray(structuredSources) ? structuredSources : []).forEach(append);

  const report = sanitizeResearchText(value);
  const markdownLinkPattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gi;
  const markdownLinks = [...report.matchAll(markdownLinkPattern)];
  markdownLinks.forEach((match) => append({ title: match[1], url: match[2], legacy: true }));

  const reportWithoutMarkdownLinks = report.replace(markdownLinkPattern, "");
  const rawUrls = reportWithoutMarkdownLinks.match(/https?:\/\/[^\s<>"'\]]+/gi) || [];
  rawUrls.forEach((url) => append({ url, legacy: true }));

  return result.slice(0, 20);
}

/**
 * One deduplicated source list for the evidence tab: detailed run sources
 * first, then sources listed with the inquiry and links quoted in older
 * reports. Conflicting sources lead, then the furthest acquisition stage.
 */
export function mergeResearchSources(report, detailSources = [], listedSources = []) {
  const merged = [];
  const seen = new Set();
  (Array.isArray(detailSources) ? detailSources : []).forEach((source) => {
    const url = normalizeHttpUrl(source?.url);
    const key = url || `id:${source?.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push({ ...source, url, domain: String(source?.domain || sourceDomain(url)).trim() });
  });
  collectResearchSources(report, listedSources).forEach((source) => {
    if (seen.has(source.url)) return;
    seen.add(source.url);
    merged.push(source);
  });
  return merged
    .map((source, index) => ({ ...source, stageKey: researchSourceStageKey(source), order: index }))
    .sort((left, right) => (
      Number(Boolean(right.conflict_detected)) - Number(Boolean(left.conflict_detected))
      || SOURCE_STAGE_ORDER.indexOf(left.stageKey) - SOURCE_STAGE_ORDER.indexOf(right.stageKey)
      || Number(right.authority_rank || 0) - Number(left.authority_rank || 0)
      || left.order - right.order
    ));
}
